import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { Component, Input, OnInit, inject } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { FuelKpiRequest } from '../../../../_shared';
import { formatCost, formatCostDifference, formatDropdownValue, getEnumFromString } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { TotalFuelLossModal } from '../../modals/total-fuel-loss-modal/total-fuel-loss-modal.component';
import { AccountFilterData, DateIntervalFilterData } from '../../../../../_shared/models';
import { FuelChangesService } from '../../../services/fuel-changes.service';
import { AuthService, FilterService } from '../../../../../_shared/services';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { isUptrendPositive } from '../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { DropdownOption, KpiWidgetComponent } from '../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { MeasurementTypes } from '../../../../../_shared/enums/kpiStats/measurement-types.enum';

@Component({
  selector: 'app-total-fuel-loss-widget',
  standalone: true,
  imports: [TranslocoPipe, KpiWidgetComponent],
  templateUrl: './total-fuel-loss-widget.component.html',
  styleUrl: './total-fuel-loss-widget.component.scss',
})
export class TotalFuelLossWidgetComponent extends BaseWidgetComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  @Input() selectedFilter: string;
  @Input() accountFilter: AccountFilterData;
  @Input() dateFilter: DateIntervalFilterData;
  @Input() modalFilterHeaders: { previous: string; current: string };

  icon: string = 'assets/images-fuel/fuelLossImg.svg';
  totalFuelLossPercentageChange: any;
  selectedUnit: string = MeasurementTypes.Volume.toString();
  hasError: boolean = false;
  errorMessage: string = '';
  currencyCode: string = '';
  currencySymbol: string = '';
  cost: number = 0;
  request: FuelKpiRequest;
  dropdownOptions: DropdownOption[] = [
    { label: this.transloco.translate('fuelDashboard.units.litres'), value: MeasurementTypes.Volume.toString(), unit: 'ℓ' }
  ];

  constructor(
    protected override filterService: FilterService,
    private fuelChangesService: FuelChangesService,
    private modalService: NgbModal,
    private errorHandlerService: ErrorHandlerService,
    private authService: AuthService
  ) {
    super(filterService);
    this.title = 'Total Fuel Losses';
    this.isLoading = true;
    this.value = 0;
    this.difference = 0;
  }

  ngOnInit(): void {
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
    this.selectedUnit = this.dropdownOptions[0].value;
  }

  async onFilterChangeAsync(): Promise<void> {
    await this.getFuelLossKpiStats();
  }

  formatValue(value: number): string {
    return formatDropdownValue(value, this.selectedUnit, this.dropdownOptions, getEnumFromString, this.currencySymbol);
  }

  formatCost(value: number): string {
    return formatCost(value, this.currencySymbol);
  }

  formatCostDifference(value: number): string {
    return formatCostDifference(value, this.selectedFilter, this.currencySymbol, (key, params) => this.transloco.translate(key, params));
  }

  get isUptrendPositive(): boolean {
    return isUptrendPositive(getEnumFromString(this.selectedUnit));
  }

  async getFuelLossKpiStats(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      this.request = {
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!,
        fuelMeasurementType: getEnumFromString(this.selectedUnit)
      };

      this.cancelSubject.next();
      const response = await this.fuelChangesService.getFuelLossKpiStats(this.request, this.cancelSubject);

      this.value = response.data?.totalValue ?? 0;
      this.cost = (response.data as any)?.totalCost ?? 0;
      this.totalFuelLossPercentageChange = response.data?.percentageChange;
      this.difference = response.data?.difference ?? 0;
      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.errorMessage = message;
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  async onDropdownChanged(selectedOption: DropdownOption): Promise<void> {
    this.selectedUnit = selectedOption.value;
    this.request.fuelMeasurementType = getEnumFromString(selectedOption.value);
    await this.getFuelLossKpiStats();
  }

  openTotalFuelLossModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
      windowClass: 'custom-modal-class',
    };
    const modalRef = this.modalService.open(TotalFuelLossModal, modalOptions);
    modalRef.componentInstance.request = this.request;
    modalRef.componentInstance.modalFilterHeaders = this.modalFilterHeaders;
  }
}